import { useState, useEffect } from "react"
import { isAuthenticated } from "../services/userService"

/**
 * Custom hook to manage authentication state
 * Listens for auth changes and updates state accordingly
 *
 * This hook automatically:
 * - Checks authentication status on mount
 * - Listens for authChange events (same tab login/logout)
 * - Listens for storage events (cross-tab login/logout)
 * - Cleans up event listeners on unmount
 *
 * @returns {boolean} isLoggedIn - Whether the user is currently authenticated
 *
 * @example
 * function MyComponent() {
 *   const isLoggedIn = useAuth()
 *
 *   return (
 *     <div>
 *       {isLoggedIn ? <LoggedInView /> : <LoggedOutView />}
 *     </div>
 *   )
 * }
 */
export function useAuth() {
    // Initialised from the token (a synchronous localStorage read) rather
    // than false, so logged-in pages don't render their logged-out view first.
    const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated)

    useEffect(() => {
        const checkAuth = () => {
            setIsLoggedIn(isAuthenticated())
        }

        // Check auth status on mount
        checkAuth()

        // Listen for auth state changes
        window.addEventListener("authChange", checkAuth)
        window.addEventListener("storage", checkAuth)

        // Cleanup listeners on unmount
        return () => {
            window.removeEventListener("authChange", checkAuth)
            window.removeEventListener("storage", checkAuth)
        }
    }, [])

    return isLoggedIn
}
